// Email Templates Bank for ControllerApp Admin Panel
// Each template includes subject, body (HTML), and metadata

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  subject: string;
  body: string;
  category: 'testflight' | 'launch' | 'general';
  isHtml: boolean;
}

export const emailTemplates: EmailTemplate[] = [
  {
    id: 'testflight-reminder',
    name: 'TestFlight Installation Reminder',
    description: 'Remind users to install the app from TestFlight',
    category: 'testflight',
    subject: 'TestFlight Installation Reminder',
    isHtml: true,
    body: `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>TestFlight Installation Reminder</title>
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
      .container { max-width: 600px; margin: 0 auto; padding: 0px; }
      .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
      .content { background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px; }
      .button { display: block; background: #007AFF; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: 600; margin: 20px auto; text-align: center; box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3); transition: all 0.2s ease; max-width: 250px; }
      .button:hover { background: #0056CC; box-shadow: 0 6px 16px rgba(0, 122, 255, 0.4); transform: translateY(-2px); }
      .logo-link {
        display: inline-block;
        text-decoration: none;
        transition: transform 0.2s ease;
      }
      .logo-link:hover {
        transform: scale(1.1);
      }
      .footer { text-align: center; margin-top: 30px; color: #666; font-size: 14px; }
      .help-section { background: #e3f2fd; padding: 5px; border-radius: 8px; margin: 5px 0; }
      .step { margin: 15px 0; padding: 10px; background: white; border-radius: 6px; border: 1px solid #e0e0e0; }
      .step strong { color: #2196f3; font-weight: bold; }
      .help-section ul { margin: 15px 0; padding: 0; }
      .help-section li { margin: 15px 0; padding: 10px; background: white; border-radius: 6px; border: 1px solid #e0e0e0; list-style: none; }
      .help-section li::before { content: "•"; color: #2196f3; font-weight: bold; margin-right: 8px; }
    </style>
  </head>
  <body>
    <div class="container">
        <div class="header">
          <a href="https://controller-app.com" class="logo-link">
            <img src="https://controller-app.com/icon.png" alt="ControllerApp" style="width: 60px; height: 60px; margin-bottom: 5px; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.2);" onerror="this.src='https://apps.apple.com/app/id899247664/icon.png'">
          </a>
          <h1 style="color: white;">Hi there!</h1>
          <p>Don't miss out on testing ControllerApp!</p>
        </div>
      <div class="content">
        <p>We noticed you haven't installed <strong>ControllerApp</strong> from TestFlight yet. Don't worry - we're here to help!</p>
        
        <div class="help-section">
          <h3>Having trouble with TestFlight?</h3>
          <p>Here's how to get started:</p>

          <div class="step">
            <strong>1.</strong> Check your email - Look for the TestFlight invitation email from Apple
          </div>

          <div class="step">
            <strong>2.</strong> Install TestFlight - Download the TestFlight app from the App Store if you haven't already
          </div>

          <div class="step">
            <strong>3.</strong> Open the invitation - Tap the link in the TestFlight email or use the button below
          </div>

          <div class="step">
            <strong>4.</strong> Install ControllerApp - Follow the prompts to install and start testing!
          </div>
        </div>

        <div style="text-align: center; margin: 30px 0;">
          <a href="https://apps.apple.com/app/id899247664" class="button" style="color: white !important; text-decoration: none;">Open TestFlight</a>
        </div>

        <div class="help-section">
          <h3>Still having issues?</h3>
          <p>If you can't find the TestFlight email or are experiencing problems:</p>
          <ul>
            <li>Check your spam/junk folder</li>
            <li>Make sure you're using the same Apple ID that you signed up with</li>
            <li>Try opening the TestFlight link on your iOS device</li>
            <li>Contact us if you need a new invitation</li>
          </ul>
        </div>

        <p>We're excited to have you test ControllerApp! If you have any questions, just reply to this email.</p>
        <p>Happy testing! 🎉</p>
      </div>
      <div class="footer">
        <p>This email was sent to help you get started with ControllerApp testing</p>
      </div>
    </div>
  </body>
</html>`
  },
  {
    id: 'app-store-launch',
    name: 'App Store Launch Announcement',
    description: 'Announce that the app is now live on the App Store',
    category: 'launch',
    subject: '🎉 ControllerApp is Now Live on the App Store!',
    isHtml: true,
    body: `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>ControllerApp Now on App Store</title>
    <style>
      * { box-sizing: border-box; }
      body { 
        font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
        line-height: 1.6; 
        color: #000000; 
        margin: 0; 
        padding: 0; 
        background: #ffffff;
      }
      .email-wrapper {
        min-height: 100vh;
        position: relative;
        background: radial-gradient(circle at 50% 50%, rgba(168, 85, 247, 0.08) 0%, transparent 25%),
                    radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.08) 0%, transparent 25%);
      }
      .container { 
        max-width: 600px; 
        margin: 0 auto; 
        padding: 0px 0px;
        position: relative;
        z-index: 10;
      }
      .header { 
        background: linear-gradient(135deg, rgba(168, 85, 247, 0.05) 0%, rgba(168, 85, 247, 0.02) 100%);
        padding: 20px 20px; 
        text-align: center; 
        border-radius: 20px;
        margin-bottom: 0px;
        border: 1px solid rgba(168, 85, 247, 0.1);
      }
      .header h1 { 
        margin: 0 0 15px 0; 
        font-size: 32px; 
        color: #000000;
        font-weight: 600;
        letter-spacing: -0.02em;
      }
      .header p { 
        margin: 8px 0; 
        font-size: 20px; 
        color: #666666;
        font-weight: 400;
      }
      .header .subtitle { 
        font-size: 16px; 
        color: #666666; 
        margin-top: 20px;
        line-height: 1.6;
      }
      .header .subtitle strong {
        color: #000000;
        font-weight: 600;
      }
      .content { 
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        padding: 0px 0px;
        border-radius: 20px;
      }
      .thank-you-box { 
        background: linear-gradient(135deg, rgba(168, 85, 247, 0.08) 0%, rgba(168, 85, 247, 0.04) 100%);
        padding: 20px; 
        border-radius: 16px; 
        margin: 30px 0; 
        text-align: center;
        border: 1px solid rgba(168, 85, 247, 0.15);
      }
      .thank-you-box strong { 
        color: #000000; 
        font-size: 18px;
        font-weight: 600;
      }
      .thank-you-box p {
        color: #333333;
        margin-top: 12px;
        line-height: 1.6;
      }
      .app-store-button { 
        display: inline-block; 
        margin: 0px auto;
        transition: transform 0.2s ease;
      }
      .app-store-button:hover { 
        transform: scale(1.05);
      }
      .logo-link {
        display: inline-block;
        text-decoration: none;
        transition: transform 0.2s ease;
      }
      .logo-link:hover {
        transform: scale(1.1);
      }
      .app-store-button img { 
        width: 180px; 
        height: auto;
      }
      .footer { 
        text-align: center; 
        padding: 0px 20px; 
        color: #666666; 
        font-size: 14px;
      }
      .footer strong {
        color: #000000;
        font-weight: 600;
      }
      .footer a { 
        color: #000000; 
        text-decoration: none;
        font-weight: 500;
      }
      .footer a:hover {
        text-decoration: underline;
      }
      .divider { 
        height: 1px; 
        background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.2), transparent);
        margin: 30px 0;
      }
      .cta-text {
        text-align: center;
        font-size: 16px;
        margin: 30px 0;
        color: #333333;
      }
      .cta-text strong {
        color: #000000;
        font-weight: 600;
      }
    </style>
  </head>
  <body>
    <div class="email-wrapper">
      <div class="container">
        <div class="header">
          <a href="https://controller-app.com" class="logo-link">
            <img src="https://controller-app.com/icon.png" alt="ControllerApp" style="width: 60px; height: 60px; margin-bottom: 15px; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.2);" onerror="this.src='https://apps.apple.com/app/id899247664/icon.png'">
          </a>
          <h1>It's Official!</h1>
          <p>ControllerApp is Now on the App Store</p>
          <div class="subtitle">
            <strong>Thank You for Beta Testing!</strong><br>
            Your feedback has been invaluable in making ControllerApp the best it can be.<br>
            <strong>Now it's time to download the official release! 🚀</strong>
          </div>
        </div>
        
        <div class="content">
          <div class="thank-you-box">
            <strong>Thank You to Our Beta Testers!</strong>
            <p>Your testing, feedback, and patience made this launch possible. You're part of the ControllerApp family!</p>
          </div>

          <div style="text-align: center; margin: 40px 0;">
            <a href="https://apps.apple.com/app/controllerapp/id6749446742" class="app-store-button">
              <img src="https://controller-app.com/app-store-badge.png" alt="Download on the App Store" style="width: 180px; height: auto;">
            </a>
          </div>

          <p style="color: #666666; text-align: center; margin-top: 30px; font-size: 15px;">
            Questions or feedback? Reply to this email – we'd love to hear from you!
          </p>
        </div>
        
        <div class="footer" style="margin-top: 15px;">
          <p><strong>ControllerApp</strong> – Professional Stage Lighting Control</p>
          <p><a href="https://controller-app.com">controller-app.com</a></p>
          <p style="margin-top: 15px; font-size: 12px; color: #999999;">
            You're receiving this email because you participated in the ControllerApp beta program.
          </p>
        </div>
      </div>
    </div>
  </body>
</html>`
  }
];

// Helper function to get template by ID
export const getTemplateById = (id: string): EmailTemplate | undefined => {
  return emailTemplates.find(template => template.id === id);
};

// Helper function to get templates by category
export const getTemplatesByCategory = (category: EmailTemplate['category']): EmailTemplate[] => {
  return emailTemplates.filter(template => template.category === category);
};

